import { ArrowPathIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { manualSyncStartFailure } from "@utils/ehrActions";
import { formatRelativeTime } from "@utils/formatRelativeTime";
import { learningTreeReviewVariables, useLearningTreeReviewEnabled } from "@utils/learningTreeReview";
import React, { useState } from "react";

import { Badge, Dialog, Input, Label, ReorderableList, Small, Tooltip } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  ApiLearnerManualGoalSyncStatusChoices, // GoalStatusEnums,
  useReorderGoalsMutation,
  useStartManualSyncWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
// import { convertReadableString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";
import { Goal, useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface GoalHeaderProps {
  goalList: Goal[];
  collapseAll: boolean;
  setCollapseAll: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string | null;
}
type GoalListItem = {
  id: string;
  title: string;
  count: number;
  status: string;
};

export const GoalHeader: React.FC<GoalHeaderProps> = ({ goalList, collapseAll, setCollapseAll, reportId }) => {
  const {
    getClientGoalDataQuery: { refetch },
    searchGoalText,
    setSearchGoalText,
    selectedGoalStatus,
    setSelectedGoalStatus,
    learnerId,
    goalRefreshStatus,
    goalRefreshLastUpdated,
    goalRefreshLoading,
    setGoalRefreshLoading,
    goalRefreshStatusQuery: { refetch: refetchStatus },
    isTreatmentPlanUploadEnabled,
    isIaToProgrammingEnabled,
  } = useClientProgrammingData();
  const { isPassageHealthUser, isEhrSyncCleared } = useAdminData();

  const startManualSyncWorkflowMutation = useStartManualSyncWorkflowMutation();
  const learningTreeReview = useLearningTreeReviewEnabled();

  const handleManualSync = () => {
    // Managed-onboarding gate: don't push a manual sync for an uncleared tenant
    // (the backend rejects it too). In practice this menu item only renders for
    // Passage — a graduated integration that is always cleared — so this guard
    // is defensive/consistency, not a path a customer normally hits.
    if (!learnerId || !reportId || !isEhrSyncCleared) return;
    setGoalRefreshLoading(true);
    startManualSyncWorkflowMutation.mutate(
      {
        learnerId,
        reportId,
        reassessmentReportGoalSync: true,
        ...learningTreeReviewVariables(learningTreeReview),
      },
      {
        onSuccess: (data) => {
          const startFailure = manualSyncStartFailure(data.startManualSyncWorkflow);
          if (startFailure) {
            setGoalRefreshLoading(false);
            notifyError(startFailure);
            return;
          }
          refetchStatus();
        },
        onError: () => {
          setGoalRefreshLoading(false);
          notifyError("Failed to start import");
        },
      },
    );
  };

  const lastUpdatedDisplay = () => {
    if (!goalRefreshLastUpdated) return "";
    return formatRelativeTime(new Date(goalRefreshLastUpdated));
  };

  const renderText = () => {
    if (goalRefreshLoading || goalRefreshStatus === ApiLearnerManualGoalSyncStatusChoices.Inprogress) {
      return (
        <div className="flex items-center gap-2">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          <Label size="extra-small" colorType="primary">
            Refreshing
          </Label>
        </div>
      );
    }
    if (goalRefreshStatus === ApiLearnerManualGoalSyncStatusChoices.Failure) {
      return (
        <Small className="text-red-500 flex flex-row gap-x-2">
          <ExclamationTriangleIcon className="text-red-500 w-5 h-5" />
          Refresh Failed
        </Small>
      );
    }
    return !!lastUpdatedDisplay() ? `Last refreshed ${lastUpdatedDisplay()}` : `Refresh pending`;
  };

  const [openReorderGoalListDialog, setOpenReorderGoalListDialog] = useState<boolean>(false);

  const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);

  const [reorderingGoalList, setReorderingGoalList] = useState<GoalListItem[]>(
    goalList.map((item) => ({
      id: item.id,
      title: item.name!,
      count: item.targets?.length || 0,
      status: item.status!,
    })),
  );
  const reorderGoalListMutation = useReorderGoalsMutation({});
  const { isPending } = reorderGoalListMutation;
  const handleReorderingList = async () => {
    reorderGoalListMutation
      .mutateAsync({
        goals: reorderingGoalList.map((item, index) => ({ goal: item.id, rankOrder: index })),
      })
      .then((res) => {
        notifySuccess(res.reorderGoals?.message || "Reordering successfully completed!");
        refetch();
        setOpenReorderGoalListDialog(false);
      });
  };

  const popoverMenuItems: PopoverMenuItem[] = [];

  const refreshGoalMenuItem = (isTreatmentPlanUploadEnabled || isIaToProgrammingEnabled) &&
    isPassageHealthUser && {
      label: "Refresh Data",
      callback: handleManualSync,
    };

  const reorderGoalMenuItem = isTreatmentPlanUploadEnabled &&
    !isIaToProgrammingEnabled && {
      label: "Reorder Goals",
      callback: () => {
        setOpenReorderGoalListDialog(true);
      },
    };

  if (reorderGoalMenuItem) {
    popoverMenuItems.push(reorderGoalMenuItem);
  }
  if (refreshGoalMenuItem) {
    popoverMenuItems.push(refreshGoalMenuItem);
  }
  popoverMenuItems.push({
    label: "Expand/Collapse All",
    callback: () => {
      setCollapseAll(!collapseAll);
    },
  });
  return (
    <div className="flex flex-row items-center py-2 gap-2">
      {isSearchEnabled || searchGoalText.length > 0 ? (
        <div className="flex w-full justify-between items-center gap-1">
          <Input
            name="search"
            label=""
            className="max-w-[80%]! min-w-[95%]! mt-0"
            formNoValidate
            value={searchGoalText}
            placeholder="Search for keyword..."
            onChange={(e) => setSearchGoalText(e.target.value)}
            autoFocus
          />
          <XMarkIcon
            onClick={() => {
              setIsSearchEnabled(false);
              setSearchGoalText("");
            }}
            className="h-6 w-6 mt-2 shrink-0 cursor-pointer! text-text-primary"
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-row items-center justify-between gap-2">
          <Tooltip text="Search" showInfoIcon={false}>
            <MagnifyingGlassIcon
              onClick={() => setIsSearchEnabled(true)}
              className="h-6 w-6 cursor-pointer! text-text-secondary"
            />
          </Tooltip>

          <div className="flex items-center gap-2">
            <div className="flex flex-row gap-2 items-center">
              {/* <Select
                title=""
                isLoading={isFetching}
                items={Object.values(GoalStatusEnums).map((status) => ({
                  primary: convertReadableString(status),
                  id: status,
                }))}
                setSelected={(selected) => {
                  setSelectedGoalStatus(selected.id as GoalStatusEnums | undefined);
                }}
                selected={
                  selectedGoalStatus
                    ? {
                        primary: convertReadableString(selectedGoalStatus),
                        id: selectedGoalStatus,
                      }
                    : undefined
                }
                placeholderText="Goal Status"
                className="max-h-14! min-w-[12.5vw]! max-w-[12.5vw]!"
              /> */}
              {selectedGoalStatus && (
                <XMarkIcon
                  className="h-6 w-6 cursor-pointer text-text-secondary"
                  onClick={() => {
                    setSelectedGoalStatus(undefined);
                  }}
                ></XMarkIcon>
              )}
            </div>
            <Label size="extra-small" colorType="primary">
              {renderText()}
            </Label>
            <PopoverMenu openDirection="to-bottom-left" menuItems={popoverMenuItems} />
          </div>
        </div>
      )}
      <Dialog
        title={
          <Label displayType="loud" colorType="primary">
            Reorder Goals
          </Label>
        }
        showCloseButton={false}
        open={openReorderGoalListDialog}
        setOpen={() => {}}
        primaryButton={{
          text: (
            <div className="flex gap-1 w-max">
              {isPending ? <ArrowPathIcon className="h-6 w-6 animate-spin p-1 text-white" /> : null}
              Apply
            </div>
          ),

          disabled: isPending,
          onClick: handleReorderingList,
        }}
        secondaryButton={{
          text: "Cancel",
          disabled: isPending,
          onClick: () => setOpenReorderGoalListDialog(false),
        }}
        size="3xl"
        overflowBehavior="hidden"
        bodyClassName="p-4"
        showDividers
      >
        <div className="max-h-[700px] overflow-y-auto">
          <ReorderableList
            items={reorderingGoalList}
            onReorder={(newItems) => setReorderingGoalList(newItems)}
            renderItem={(goal) => {
              const Icon = goal.status ? getStatusIcon(goal.status) : React.Fragment;

              return (
                <div className="flex w-full flex-row items-center justify-between gap-2 ">
                  <span className="max-w-md font-medium text-gray-800 ph-no-capture">{goal.title}</span>
                  <div className="gap- flex shrink-0 flex-row gap-1">
                    {goal.status ? (
                      <Badge
                        text={goal.status ? <Icon className="h-2 w-2" /> : null}
                        appearance={goal.status ? getStatusAppearance(goal.status) : "neutral"}
                      />
                    ) : null}
                    <span className="text-xs text-gray-500">{goal.count} targets</span>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </Dialog>
    </div>
  );
};
